import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Input, Button, message, Modal } from "antd";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import { clock } from "../../utils/clock";
import { v4 as uuidv4 } from "uuid";
import POSLogo from "../../assets/images/icon.png";
import db, { isDbBridgeAvailable } from "../../database";
import data from "../../constants/opentillClosetill.json";
import "../style.css";
import "../Till/TillScreens.css";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { upsertTill, upsertPOSLogService } from "../../services";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useSessionStore from "../../stores/useSessionStore";
import { getMultilingualText } from "../../utils/getMultilingualText";
import { resolveActingUserId } from "../../utils/actingUser";
import { closeTillPendingNotice, raiseCountersAfterTillOpen, readServerCashToKeep } from "../TillMovedGuard";

const OpenTillComponent = () => {
  const history = useHistory();
  const [openTillForm] = Form.useForm();
  const tillData = useAuthStore((s) => s.tillData);
  const currenciesList = tillData?.tillAccess?.csBunit?.currencies || [];
  const currency = currenciesList[0] || {};
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
  const tillLayout = parseInt(tillaccess?.layout === null || undefined ? 1 : tillaccess?.layout);
  const [inputValue, setInputValue] = useState({});
  const [tillLoading, setTillLoading] = useState(false);
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const [inputAmount, setInputAmount] = useState(posConfig?.retainCashforNextShift === "Y" ? useAuthStore.getState().tillData?.tillAccess?.cwrTill?.cashToKeep || 0 : 0);
  const [noteValue, setNoteValue] = useState("");
  const authHeaders = getOAuthHeaders();
  const amountRef = useRef(null);

  const amountTypedRef = useRef(false);
  const paymentMethodList = tillData?.tillAccess?.csBunit?.paymentMethodList || [];

  useEffect(() => {
    amountRef.current.focus();
    (async () => {
      const tillSession = useSessionStore.getState().tillSession;
      if (tillSession && tillSession.tillStatus === "open") {
        try {
          const matchingEvents = await db.tillEvents
            .where("tillSessionId")
            .equals(tillSession.tillSessionId)
            .toArray();
          const openEvent = matchingEvents?.find((e) => e.tillStatus === "open");
          if (openEvent) {
            history.push("/pos");
            return;
          }
          if (!isDbBridgeAvailable()) {
            // BUG-2081: empty result may mean the Electron IPC bridge is
            // momentarily unreachable, not that the session is actually stale.
            // Trust the existing Zustand session and let PrivateRoute's own
            // bridge-aware re-check be the authority once /pos mounts, instead
            // of clearing a possibly-still-valid session here.
            history.push("/pos");
            return;
          }
        } catch (err) {
          console.error("[OpenTillComponent] Error verifying tillSession:", err);
        }
        useSessionStore.getState().setTillSession(null);
      }
      if (!tillSession || !useSessionStore.getState().tillSession) {
        db.tillRegistrations.toArray().then((reg) => {
          if (reg.length > 0) {
            const curList = reg[0].tillAccess[0].csBunit.csCurrency;
            const csCurrencyId = curList.csCurrencyId;
            localStorage.setItem("csCurrencyId", csCurrencyId);
          }
        });
      }
    })();
  }, []);

  // BUG-2443: the pre-fill above was read at sign-in. If the till's day was closed on
  // another device since, take the server's Cash to Keep -- unless the cashier has
  // already typed an amount.
  useEffect(() => {
    let gone = false;
    readServerCashToKeep().then((amount) => {
      if (amount != null && !gone && !amountTypedRef.current) setInputAmount(amount);
    });
    return () => {
      gone = true;
    };
  }, []);

  const [denominations, setDenominations] = useState([]);

  useEffect(() => {
    const modifiedCurrenciesList = (currency.denominations || []).map((denomItem, denomIndex) => ({
      ...denomItem,
      count: 0,
      amount: 0,
    }));
    setDenominations(modifiedCurrenciesList);
  }, []);

  const [displayClock, setDisplayClock] = useState("");
  useEffect(() => {
    setDisplayClock(clock());
    const timerId = setInterval(() => setDisplayClock(clock()), 1000);
    return () => {
      clearTimeout(timerId);
    };
  }, []);

  const [totalAmount, setTotalAmount] = useState(0);

  const onFieldInput = (e) => {
    let amountValue = posConfig?.showDayOpeningAmount === "Y" ? inputAmount : 0;
    const denominationValue = parseInt(amountValue);
    const denominationAmount = denominationValue;
    if (denominations && denominations.length > 0) {
      denominations[0].count = 1;
      denominations[0].amount = isNaN(denominationAmount) ? 0 : denominationAmount;
    }
    setTotalAmount(inputAmount);
    setDenominations([...denominations]);
    excuteOpenTill(amountValue, denominations);
  };

  const excuteOpenTill = async (inputAmount, denominationsData) => {
    if (tillLoading) return;
    setTillLoading(true);
    if (authHeaders && authHeaders.access_token) {
      const tillSessionId = uuidv4().replace(/-/g, "").toUpperCase();
      const tillData = useAuthStore.getState().tillData;
      const userData = useAuthStore.getState().userData;
      const openTillData = {
        tillSessionId: tillSessionId,
        tillStatus: "open",
        isSynced: 0,
        openingTime: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
        // Business date this session is booked under (same source as tillCash.date below).
        // For a next-business-date session it is a future date; the close screen uses this
        // to hide "Close Store" until the calendar catches up. Set fresh every open — no stale risk.
        businessDate: useSessionStore.getState().nextBusinessDate
          ? dayjs(useSessionStore.getState().nextBusinessDate).format("YYYY-MM-DD")
          : dayjs(new Date()).format("YYYY-MM-DD"),
        closingTime: null,
        openingCashDenominations: denominationsData,
        closingCashDenominations: null,
        openingCash: parseFloat(inputAmount) >= 0 ? parseFloat(inputAmount) : 0,
        closingCash: 0,
        totalOpeningAmount: parseFloat(inputAmount) >= 0 ? parseFloat(inputAmount) : 0,
        totalClosingExpectedAmount: 0,
        totalClosingTransactions: 0,
        totalClosingAmount: 0,
        note: noteValue,
        user: useAuthStore.getState().userData?.user,
      };
      const ordersData = {
        tillSessionId: tillSessionId,
        tillStatus: "open",
        date: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
        netTotal: 0,
        grossTotal: 0,
        totalTax: 0,
        items: [],
        netReturnTotal: 0,
        grossReturnTotal: 0,
        totaReturnlTax: 0,
        salesTransactions: 0,
        returnsTransactions: 0,
        salesTax: [],
        returnTax: [],
      };

      const paymentMethodList = tillData?.tillAccess?.csBunit?.paymentMethodList || [];
      const paymentsData = {
        tillSessionId: tillSessionId,
        tillStatus: "open",
        date: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
        payments: paymentMethodList,
      };
      // localStorage.setItem("tillSession", JSON.stringify(openTillData));
      if (usePOSConfigStore.getState().posConfig?.DON === "Y") {
        const timeMark = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        const currentDate = new Date().toLocaleDateString("zh-Hans-CN");
        const trxId = uuidv4().replace(/-/g, "").toUpperCase();
        const dayOpeningArray = [
          `{
          type: "DON", action: "LOG", description: "${timeMark}",
          date: "${currentDate}", time: "${timeMark}", orderNo: "",
          remarks: "", transactionId: "${trxId}", status: "SCS", duration: null
        }`,
        ];
        upsertPOSLogService(tillData.tillAccess.cwrTill.cwrTillID, tillData.tillAccess.csUserId, tillData.tillAccess.csBunit.csBunitId, dayOpeningArray);
      }
      let index = paymentMethodList.findIndex((payment) => payment.name?.toLowerCase() === "cash");
      if (index < 0) index = 0;
      const nextBusinessDate = useSessionStore.getState().nextBusinessDate || dayjs(new Date()).format("YYYY-MM-DD");
      const taxInfoData = {
        cwrTillID: tillData?.tillAccess?.cwrTill?.cwrTillID || "",
        csbunitID: tillData?.tillAccess?.csBunit?.csBunitId || "",
        csclientID: tillData?.tillAccess?.csClientId || "",
        created: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
        // BUG-2232: the cashier actually opening the till, not the till-registration user.
        createdby: resolveActingUserId(tillData),
        updated: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
        updatedby: resolveActingUserId(tillData),
        openTill: "Y",
        storeDailyOpsTillid: null,
        tillCash: [
          {
            cwrTillCashId: uuidv4().replace(/-/g, "").toUpperCase(),
            date: nextBusinessDate,
            finPaymentmethodId: paymentMethodList[index]?.finPaymentmethodId || null,
            finFinancialAccount2Id: null,
            opening: parseFloat(inputAmount) >= 0 ? parseFloat(inputAmount) : 0,
            opentime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            closetime: null,
            notes: noteValue ? noteValue : null,
            sales: 0,
            netsales: 0,
            cashin: 0,
            cashout: 0,
            retainAmt: 0,
            closing: 0,
            returns: 0,
            iscash: 1,
            isclose: 0,
            pettyCashIn: 0,
            pettyCashOut: 0,
            cashInOut: [],
            cashEvents: [],
          },
        ],
      };
      const stringifiedData = JSON.stringify(taxInfoData);
      const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
      const newStringRequest = JSON.stringify(newStringifiedFields);
      try {
        const response = await upsertTill(newStringRequest);
        const upsertTillResponse = response?.data?.data?.upsertTill;

        if (upsertTillResponse?.status === "200") {
          setInputAmount(0);
          const storeDailyOpsTillid = upsertTillResponse.tillCash[0]?.storeDailyOpsTillid;
          useSessionStore.getState().setTillSession(openTillData);
          // A brand-new till session (fresh tillSessionId, just opened) is the one
          // unambiguous signal that any prior Day End Application lock (see
          // cashToKeep.js) is behind us — clear it here regardless of which path
          // (SSO cookie vs explicit login) got the user back to open this till.
          useSessionStore.getState().setDayEndAppUrl(null);
          await db.tillEvents.add({
            ...openTillData,
            storeDailyOpsTillid,
            serverTillCash: upsertTillResponse.tillCash,
          });
          await db.ordersData.add({ ...ordersData, storeDailyOpsTillid });
          await db.paymentsData.add({ ...paymentsData, storeDailyOpsTillid });
          // if (socket) {
          //   const tillId = tillData?.tillAccess?.cwrTill?.till;
          //   const storeId = tillData?.tillAccess?.csBunit?.csBunitId;
          //   if (tillId && storeId) {
          //     updateSocketAuth(tillId, storeId);
          //   }
          // }
          // BUG-2443: raise-only; a till taken over from another device may hold counters
          // read before that device's last bills reached the server.
          await raiseCountersAfterTillOpen();
          history.push("/pos");
        } else {
          const pending = closeTillPendingNotice(upsertTillResponse?.message);
          if (pending) Modal.warning({ ...pending, centered: true });
          else message.error(upsertTillResponse?.message || "Failed to open till");
          useSessionStore.getState().setTillSession(null);
          setTillLoading(false); // Reset loading so user can retry
        }
      } catch (error) {
        console.error("Error during upsertTill:", error);
        message.error("Failed to open till. Please try again.");
        setTillLoading(false);
      }
    } else {
      setTillLoading(false);
    }
  };

  let reconstructedObject;
  const rawtillAccessMeta = tillData?.tillAccess?.tillAccessMeta;

  if (rawtillAccessMeta) {
    const rawDataArray = tillData?.tillAccess?.tillAccessMeta;

    // Find the object with the key "Open Till"
    const openTillObject = rawDataArray.find((item) => item.key === "Open Till");

    // Access the value property of the found object
    const openTillValue = openTillObject?.value;

    if (openTillValue !== undefined) {
      // Clean up the string (remove extra characters)
      const cleanedData = openTillValue.replace(/[{}"]/g, "");

      // Split the string into key-value pairs
      const keyValuePairs = cleanedData.split(",");

      // Create an object from key-value pairs
      reconstructedObject = {};
      keyValuePairs.forEach((pair) => {
        // Check if the pair contains a colon
        if (pair.includes(":")) {
          const [key, value] = pair.split(":");

          // Check if both key and value are not undefined before trimming
          if (key !== undefined && value !== undefined) {
            reconstructedObject[key.trim()] = value.trim();
          }
        }
      });
    } else {
      // Handle the case where openTillValue is undefined
      console.error("openTillValue is undefined");
    }
  }

  return (
    <div className="till-screen">
      <div className="till-open-form">
        <div className="till-open-form__left">
          {/* BUG-2061: was marginBottom: "24.5%" — percentage margins resolve
              against the container's WIDTH, not height, so this gap scaled
              with screen width instead of the vertical space it balances,
              breaking on non-16:9 resolutions. vh keeps it tied to height. */}
          <div className="till-open-form__header" style={{ marginBottom: "22vh" }}>
            <img src={POSLogo} onClick={() => history.push("/")} className="till-header__logo" style={{ cursor: "pointer" }} alt="" />
            <span className="till-open-form__header-title">{getMultilingualText("Open Till")}</span>
          </div>
          <Row style={{ paddingTop: "1vh" }}>
            <Col offset={4} span={18} style={{ display: "flex", flexDirection: "column" }} id="step1">
              <Form form={openTillForm} name="oprnTillForm">
                <Form.Item style={{ width: "100%" }} name="amount" id="step1">
                  <p className="till-open-form__field-label">{getMultilingualText("Amount")}</p>
                  <Input
                    placeholder={currency.symbolRightSide === "N" ? `${currency.currSymbol}0` : currency.symbolRightSide === "Y" ? `0${currency.currSymbol}` : ""}
                    onChange={(e) => {
                      amountTypedRef.current = true;
                      setInputValue(e.target);
                      setInputAmount(e.target.value);
                    }}
                    ref={amountRef}
                    value={inputAmount}
                    className="transactionAmtInput"
                    style={{ marginBottom: "3vh", height: "8.5vh" }}
                  />
                </Form.Item>
                <Form.Item style={{ width: "100%" }} name="note">
                  <p className="till-open-form__field-label">{getMultilingualText("Note")}</p>
                  <Input
                    placeholder="Enter note"
                    onChange={(e) => setNoteValue(e.target.value)}
                    className="transactionAmtInput"
                    style={{ marginBottom: "1.7vh", height: "8.5vh" }}
                  />
                </Form.Item>
                <Button id="step3" htmlType="submit" onClick={onFieldInput} className="till-btn--success" loading={tillLoading} disabled={tillLoading}>
                  {tillLoading ? getMultilingualText("Opening Till...") : getMultilingualText("Open Till")}
                </Button>
              </Form>
            </Col>
          </Row>
        </div>
        <div className="till-open-form__right">
          <div id="step2" className="till-open-form__details-card">
            <p className="till-open-form__details-title">{getMultilingualText("Last Closure Details")}</p>
            <Row>
              <Col span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Outlet")}</p>
              </Col>
              <Col span={11}>
                <p className="till-open-form__summary-value">{tillData.tillAccess.csBunit.name}</p>
              </Col>
              <Col span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Register")}</p>
              </Col>
              <Col span={11}>
                <p className="till-open-form__summary-value">{tillData.tillAccess.cwrTill.till}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.OpeningAmount === "Y" ? "block" : "none") : "block" }} span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Opening Amount")}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.OpeningAmount === "Y" ? "block" : "none") : "block" }} span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}
                  {inputAmount?.length <= 0 ? 0 : inputAmount}
                  {currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.CashIn === "Y" ? "block" : "none") : "block" }} span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Cash In")}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.CashIn === "Y" ? "block" : "none") : "block" }} span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}0{currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.CashOut === "Y" ? "block" : "none") : "block" }} span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Cash Out")}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.CashOut === "Y" ? "block" : "none") : "block" }} span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}0{currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.PettyCashIn === "Y" ? "block" : "none") : "block" }} span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Petty Cash In")}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.PettyCashIn === "Y" ? "block" : "none") : "block" }} span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}0{currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.pettyCashOut === "Y" ? "block" : "none") : "block" }} span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Petty Cash Out")}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.pettyCashOut === "Y" ? "block" : "none") : "block" }} span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}0{currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.ExpectedCash === "Y" ? "block" : "none") : "block" }} span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Expected Cash")}</p>
              </Col>
              <Col style={{ display: reconstructedObject ? (reconstructedObject.ExpectedCash === "Y" ? "block" : "none") : "block" }} span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}0{currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
              <Col span={11} offset={1}>
                <p className="till-open-form__summary-label">{getMultilingualText("Actual Cash")}</p>
              </Col>
              <Col span={11}>
                <p className="till-open-form__summary-value">
                  {currency.symbolRightSide === "N" ? currency.currSymbol : ""}0{currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                </p>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenTillComponent;
